import { useState, useEffect, useRef } from 'react';
import { useModalStore } from '../../../stores/modalStore.js';
import { useServerStore } from '../../../stores/serverStore.js';
import { useFocusTrap } from '../../../hooks/useFocusTrap.js';

const AUTH = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

const DEMO_STAGE = {
  id: 'stage-demo',
  topic: 'Dev Office Hours — Ask Us Anything',
  speakers: [
    { id: 'sp1', name: 'Anderson', avatar: 'A', isMuted: false },
    { id: 'sp2', name: 'Trevor',   avatar: 'T', isMuted: true  },
  ],
  audience: [
    { id: 'au1', name: 'Marcus',   avatar: 'M' },
    { id: 'au2', name: 'Sarah',    avatar: 'S' },
    { id: 'au3', name: 'Jordan',   avatar: 'J' },
    { id: 'au4', name: 'Casey',    avatar: 'C' },
  ],
  handRaises: ['au3'],
};

function Mic({ muted }) {
  return <span style={{ fontSize: 11 }}>{muted ? '🔇' : '🎙️'}</span>;
}

function SpeakerTile({ person, isHost, onMoveToAudience }) {
  const initial = person.name?.[0]?.toUpperCase() || '?';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: 10, borderRadius: 10, background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)', minWidth: 80 }}>
      <div style={{ position: 'relative' }}>
        <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 700, color: '#fff', border: person.isMuted ? '2px solid rgba(255,255,255,0.1)' : '2px solid #6366f1' }}>
          {person.avatar || initial}
        </div>
        <span style={{ position: 'absolute', bottom: 0, right: 0, background: person.isMuted ? '#374151' : '#10b981', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, border: '2px solid #16213e' }}>
          <Mic muted={person.isMuted} />
        </span>
      </div>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#e2e8f0', textAlign: 'center', maxWidth: 70, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{person.name}</div>
      {isHost && (
        <button onClick={() => onMoveToAudience(person.id)} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', cursor: 'pointer' }}>
          Move to audience
        </button>
      )}
    </div>
  );
}

function AudienceMember({ person, hasRaisedHand, isHost, onInviteToSpeak }) {
  const initial = person.name?.[0]?.toUpperCase() || '?';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 7, background: hasRaisedHand ? 'rgba(245,158,11,0.1)' : 'rgba(255,255,255,0.03)', border: `1px solid ${hasRaisedHand ? 'rgba(245,158,11,0.3)' : 'rgba(255,255,255,0.06)'}` }}>
      <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'linear-gradient(135deg,#374151,#1f2937)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#9ca3af', flexShrink: 0 }}>{person.avatar || initial}</div>
      <div style={{ flex: 1, fontSize: 13, color: 'rgba(255,255,255,0.65)' }}>{person.name}</div>
      {hasRaisedHand && <span style={{ fontSize: 14 }} title="Hand raised">✋</span>}
      {isHost && hasRaisedHand && (
        <button onClick={() => onInviteToSpeak(person.id)} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 5, background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.4)', color: '#a5b4fc', cursor: 'pointer', fontWeight: 600 }}>
          Invite to speak
        </button>
      )}
    </div>
  );
}

export function StageModal() {
  const { isOpen, type, data, onClose } = useModalStore();
  const currentServerId = useServerStore(s => s.currentServerId);
  const [stage, setStage] = useState(null);
  const [handRaised, setHandRaised] = useState(false);
  const [topic, setTopic] = useState('');
  const [view, setView] = useState('stage');  // stage | start
  const ref = useRef();
  useFocusTrap(ref, isOpen && type === 'stage');

  const serverId = data?.serverId || currentServerId;
  const isDemoServer = serverId?.startsWith('demo-');
  const userId = localStorage.getItem('user_id') || 'me';

  useEffect(() => {
    if (!isOpen || type !== 'stage') return;
    setHandRaised(false);
    if (isDemoServer) {
      setStage(DEMO_STAGE);
      setView('stage');
    } else if (data?.stage) {
      setStage(data.stage);
      setView('stage');
    } else {
      setStage(null);
      setView('start');
    }
  }, [isOpen, type]);

  if (!isOpen || type !== 'stage') return null;

  const isHost = stage?.speakers?.[0]?.id === userId || isDemoServer;
  const isSpeaker = stage?.speakers?.some(s => s.id === userId);

  const handleStart = async () => {
    if (!isDemoServer) {
      const r = await fetch(`/api/servers/${serverId}/stages`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...AUTH() },
        body: JSON.stringify({ topic, channelId: data?.channelId }),
      });
      const d = await r.json();
      if (d.success) setStage(d.stage);
    } else {
      setStage({ ...DEMO_STAGE, topic: topic || DEMO_STAGE.topic });
    }
    setView('stage');
  };

  const handleEnd = async () => {
    if (!isDemoServer && stage?.id) {
      await fetch(`/api/servers/${serverId}/stages/${stage.id}`, { method: 'DELETE', headers: AUTH() });
    }
    onClose();
  };

  const handleRaiseHand = () => {
    setHandRaised(h => !h);
    if (!isDemoServer && stage?.id) {
      const method = handRaised ? 'DELETE' : 'POST';
      fetch(`/api/servers/${serverId}/stages/${stage.id}/hand`, { method, headers: AUTH() });
    }
  };

  const handleInviteToSpeak = (targetUserId) => {
    if (isDemoServer) {
      setStage(s => {
        const member = s.audience.find(a => a.id === targetUserId);
        if (!member) return s;
        return {
          ...s,
          speakers: [...s.speakers, { ...member, isMuted: true }],
          audience: s.audience.filter(a => a.id !== targetUserId),
          handRaises: s.handRaises.filter(id => id !== targetUserId),
        };
      });
    } else {
      fetch(`/api/servers/${serverId}/stages/${stage.id}/speak`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...AUTH() },
        body: JSON.stringify({ userId: targetUserId }),
      });
    }
  };

  const handleMoveToAudience = (targetUserId) => {
    if (isDemoServer) {
      setStage(s => {
        const member = s.speakers.find(sp => sp.id === targetUserId);
        if (!member) return s;
        return { ...s, speakers: s.speakers.filter(sp => sp.id !== targetUserId), audience: [...s.audience, member] };
      });
    }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label="Stage Channel"
        onClick={e => e.stopPropagation()}
        style={{ background: '#0f1829', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 16, width: 600, maxWidth: '95vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 40px 100px rgba(0,0,0,0.8)', overflow: 'hidden' }}
      >
        {/* Stage header */}
        <div style={{ padding: '18px 22px', background: 'linear-gradient(135deg,rgba(99,102,241,0.15),rgba(139,92,246,0.08))', borderBottom: '1px solid rgba(99,102,241,0.15)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#6366f1', boxShadow: '0 0 8px #6366f1', animation: 'pulse 2s infinite' }} />
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{stage?.topic || 'Stage Channel'}</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 1 }}>
                  {view === 'stage' ? `${(stage?.speakers || []).length} speakers · ${(stage?.audience || []).length} listening` : 'Start a stage'}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {view === 'stage' && isHost && (
                <button onClick={handleEnd} style={{ padding: '6px 14px', borderRadius: 7, background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.4)', color: '#f87171', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  End Stage
                </button>
              )}
              <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>✕</button>
            </div>
          </div>
        </div>

        {/* Start view */}
        {view === 'start' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '30px 40px', gap: 20 }}>
            <div style={{ fontSize: 48 }}>🎙️</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#fff', textAlign: 'center' }}>Start a Stage</div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>Broadcast to your server. Audience can listen, speakers can talk.</div>
            <input
              value={topic}
              onChange={e => setTopic(e.target.value)}
              placeholder="Stage topic (optional)"
              style={{ width: '100%', padding: '10px 14px', borderRadius: 8, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 14, outline: 'none', textAlign: 'center', maxWidth: 360 }}
            />
            <button
              onClick={handleStart}
              style={{ padding: '10px 30px', borderRadius: 8, background: '#6366f1', border: 'none', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
            >
              🎙️ Go Live
            </button>
          </div>
        )}

        {/* Stage view */}
        {view === 'stage' && stage && (
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px 22px' }}>
            {/* Speakers */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Speakers — {stage.speakers.length}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {stage.speakers.map(sp => (
                  <SpeakerTile key={sp.id} person={sp} isHost={isHost && sp.id !== stage.speakers[0]?.id} onMoveToAudience={handleMoveToAudience} />
                ))}
              </div>
            </div>

            {/* Audience */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Audience — {stage.audience.length}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {stage.audience.map(au => (
                  <AudienceMember key={au.id} person={au} hasRaisedHand={(stage.handRaises || []).includes(au.id)} isHost={isHost} onInviteToSpeak={handleInviteToSpeak} />
                ))}
                {stage.audience.length === 0 && (
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.25)', fontStyle: 'italic' }}>No audience members yet.</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer controls */}
        {view === 'stage' && (
          <div style={{ padding: '14px 22px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, flexShrink: 0 }}>
            {!isSpeaker && (
              <button
                onClick={handleRaiseHand}
                style={{ padding: '8px 20px', borderRadius: 8, background: handRaised ? 'rgba(245,158,11,0.25)' : 'rgba(255,255,255,0.08)', border: `1px solid ${handRaised ? 'rgba(245,158,11,0.5)' : 'rgba(255,255,255,0.15)'}`, color: handRaised ? '#fbbf24' : 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                {handRaised ? '✋ Hand Raised' : '✋ Raise Hand'}
              </button>
            )}
            <button
              onClick={onClose}
              style={{ padding: '8px 20px', borderRadius: 8, background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            >
              Leave Quietly
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
